import React from 'react';
import PageHeader from 'app/components/PageHeader';
import { Title } from 'app/components/Typography/Title';
import styled from '@emotion/styled';
import Steps from 'app/components/Steps';
import { OrderSections, TemplateInformationSteps } from '../../constants';
import { Form, notification, Space, Spin } from 'antd';
import Button from 'app/components/Button';
import { Caption } from 'app/components/Typography/Caption';
import get from 'lodash/get';

import {
  useCreateTemplateMutation,
  useLazyGetDefaultTemplateQuery,
  useLazyGetTemplateQuery,
  useUpdateTemplateMutation
} from 'app/features/templates/api';
import ModalForm from '../../components/ModalForm';
import ModalSelectProduct from '../../components/ModalSelectProduct';
import { IProduct } from 'app/types/interfaces/Product';
import { useNavigate, useParams } from 'react-router';
import { formatDate } from 'app/utils';
import Modal from 'app/components/Modal';
import { useSearchParams } from 'react-router-dom';
import useUploadFile from 'app/hooks/useUploadFile';
import { AuthContext } from 'app/providers/Authentication';

/**
 * Template Information View is viewing/creating/editing a template.
 */
const Content = styled.div`
  width: 60vw;
  margin: auto;
  box-shadow: 0px 1px 6px 0px rgba(165, 178, 200, 0.2);
  box-shadow: 0px 2px 4px 1px rgba(165, 178, 200, 0.1);
  background: #ffffff;
  border-radius: 4px;
  padding: 40px;
  margin-bottom: 20px;
`;
const StyledSteps = styled(Steps)`
  width: 30vw;
  margin: auto;
  margin-top: 40px;
  margin-bottom: 80px;

  .ant-steps-item-title {
    line-height: 1.125rem;
    font-size: 12px;
    font-weight: 500;
    line-height: 14px;
    letter-spacing: 0em;
    text-align: center;
  }
`;
const StyledFooter = styled.div`
  width: 60vw;
  margin: auto;
  margin-bottom: 40px;
  > span[data-caption='caption'] {
    font-size: 8px;
    font-weight: 400;
    line-height: 14px;
    letter-spacing: 0em;
    text-align: center;
    color: rgba(137, 145, 160, 1);
  }
`;
const TemplateInformation = () => {
  const authContext = React.useContext(AuthContext);
  const navigate = useNavigate();
  const params: Record<string, any> = useParams();
  const [search] = useSearchParams();
  const duplicateId = search.get('duplicate');

  const formValues = React.useRef<any>(new Array(2).fill(null));
  const [isReady, setIsReady] = React.useState(false);
  const [fetch, resultDefaultTemplate] = useLazyGetDefaultTemplateQuery();
  const [fetchTemplate, resultTemplate] = useLazyGetTemplateQuery();
  const [createTemplate, resultCreateTemplate] = useCreateTemplateMutation();
  const [updateTemplate, resultUpdateTemplate] = useUpdateTemplateMutation();

  const [isOpenProductModal, setIsOpenProductModal] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState(0);
  const { uploadFile, isLoading } = useUploadFile();

  const [visibleModal, setVisibleModal] = React.useState('');
  const showWarningPopup = (e: any) => {
    e.preventDefault();
    Modal.danger({
      type: 'confirm',
      title: 'Are sure you want to back ?',
      content: 'Warning! Current data has not been saved yet.',
      onOk: () => {
        navigate('/dashboard/template-management');
      },
      okText: 'Confirm'
    });
  };
  const onBack = (e: any) => {
    showWarningPopup(e);
  };
  const [form] = Form.useForm();

  const initialValues = React.useMemo(() => {
    const defaultTemplate = resultTemplate?.data || resultDefaultTemplate?.data;
    //TODO: Will Parser Default Template from BE
    if (!defaultTemplate) {
      return {
        name: '',
        scheme: [],
        products: []
      };
    }
    const data = defaultTemplate;
    const { scheme } = data;
    const parsed = JSON.parse(`${scheme.replace(/'/g, '')}`);
    //hack to set name when go back.
    const name =
      duplicateId !== null
        ? `${defaultTemplate.name} (1)`
        : defaultTemplate.name;
    form.setFieldValue('name', name);
    return {
      name,
      scheme: parsed,
      products: defaultTemplate.productProfiles ?? []
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultDefaultTemplate, resultTemplate]);

  const StepContent = React.useMemo(() => {
    const elementProps = {
      onEdit: (sectionName: string) => {
        setVisibleModal(sectionName);
      },
      onSelectProduct: () => {
        setIsOpenProductModal(true);
      },
      onRemoveProduct: (item: IProduct) => {
        form.setFieldValue(
          'products',
          form
            .getFieldValue('products')
            ?.filter((product: IProduct) => product.id !== item.id)
        );
      },
      form
    };
    const Element = React.createElement(
      TemplateInformationSteps[currentStep]?.content as any,
      elementProps
    );
    return Element;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const handleReferenceFile = async (item) => {
    const { value } = item;
    if (item.name === 'referenceFile') {
      const uploaded = value?.filter(({ originFileObj }) => !originFileObj);
      const willUpload = value
        ?.filter(({ originFileObj }) => !!originFileObj)
        .map(({ originFileObj }) => originFileObj);

      if (!willUpload?.length) return Promise.resolve(item);
      const processedFiles = await uploadFile(
        willUpload,
        authContext.company.id
      );

      return Promise.resolve({
        ...item,
        value: [...uploaded, ...processedFiles.map((file) => file.id)]
      });
    }
    return Promise.resolve(item);
  };

  const handleTransformField = async (field) => {
    const { items } = field;
    const preparedItems = await Promise.all(
      items.map(async (item) => {
        return await Promise.all(item.map(handleReferenceFile));
      })
    );
    return Promise.resolve({
      ...field,
      items: preparedItems
    });
  };

  const handleUploadOnSiteTest = async (scheme) => {
    if (Array.isArray(scheme) && scheme.length > 0) {
      const onSiteTestIndex = scheme.findIndex(
        (item) => item.name === 'onSiteTest'
      );
      const section = scheme[onSiteTestIndex];
      const { fields } = section;
      const preparedFields = await Promise.all(
        fields.map(handleTransformField)
      );
      scheme[onSiteTestIndex] = {
        ...section,
        fields: preparedFields
      };
    }
    return scheme;
  };

  const saveTemplate = async () => {
    const valuesStep1 = formValues.current[0];
    const valuesStep2 = formValues.current[1] || initialValues;
    const parsedValue = Object.keys(valuesStep1.scheme).reduce(
      (acc: any[], current: any) => {
        acc.push({
          ...valuesStep1.scheme[current]
        });
        return acc;
      },
      []
    );
    const scheme = await handleUploadOnSiteTest(parsedValue);
    if (params?.id || resultCreateTemplate.data?.id) {
      return updateTemplate({
        templateId: params.id || resultCreateTemplate.data.id,
        data: {
          ...valuesStep1,
          productIds:
            valuesStep2?.products?.map(({ id }: IProduct) => id) ?? [],
          scheme
        }
      }).unwrap();
    }
    return createTemplate({
      ...valuesStep1,
      productIds: valuesStep2?.products?.map(({ id }: IProduct) => id) ?? [],
      scheme
    }).unwrap();
  };

  const lastSaved = React.useMemo(() => {
    const time =
      resultUpdateTemplate?.data?.updatedAt ||
      resultCreateTemplate?.data?.updatedAt ||
      resultTemplate?.data?.updatedAt ||
      '';

    return !time
      ? 'Not yet saved'
      : `Last saved: ${formatDate(time, 'HH:MM, DD/MM/YYYY')}`;
  }, [resultCreateTemplate, resultUpdateTemplate, resultTemplate]);

  React.useEffect(() => {
    if (params.id) {
      fetchTemplate(params.id);
    } else if (duplicateId) {
      fetchTemplate(duplicateId);
    } else {
      fetch(null);
    }
    return () => {
      form.resetFields();
      setIsReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        ...initialValues,
        scheme: (initialValues as any).scheme.sort((a: any, b: any) => {
          const orderA: string = get(OrderSections, a.name, '');
          const orderB: string = get(OrderSections, b.name, '');
          return orderA.localeCompare(orderB);
        })
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  React.useEffect(() => {
    const defaultTemplate = resultTemplate?.data || resultDefaultTemplate?.data;
    if (defaultTemplate) {
      setIsReady(true);
    }
  }, [resultDefaultTemplate, resultTemplate]);
  return (
    <div>
      <PageHeader onBack={onBack} title="Back">
        <>
          <Title level={3}>Template Information</Title>
          {
            //Create empty block.
          }
          <div></div>
        </>
      </PageHeader>
      {isReady && (
        <Spin
          spinning={
            isLoading ||
            resultDefaultTemplate.isLoading ||
            resultTemplate.isLoading ||
            resultCreateTemplate.isLoading ||
            resultUpdateTemplate.isLoading
          }
        >
          <Form.Provider
            onFormFinish={(name, info) => {
              if (name !== 'main') {
                const values = form.getFieldValue('scheme');
                form.setFieldValue(
                  'scheme',
                  values.reduce((acc: any, current: any) => {
                    if (current.name === name) {
                      acc.push({ ...current, ...info.values });
                      return acc;
                    }
                    acc.push(current);
                    return acc;
                  }, [])
                );
              }
            }}
          >
            <Form name="main" layout="vertical" form={form}>
              <StyledSteps
                onChange={(nextStep) => {
                  const values = form.getFieldsValue();
                  formValues.current[currentStep] = {
                    ...values
                  };
                  setCurrentStep(nextStep);
                }}
                labelPlacement="vertical"
                current={currentStep}
              >
                {TemplateInformationSteps.map(({ title, key }) => {
                  return <Steps.Step key={key} title={title}></Steps.Step>;
                })}
              </StyledSteps>
              <Content>{StepContent}</Content>
              <StyledFooter>
                <Space direction="vertical">
                  <Space size={8}>
                    <Button
                      onClick={async () => {
                        const values = form.getFieldsValue();
                        formValues.current[currentStep] = {
                          ...values
                        };
                        const { data } = await saveTemplate();
                        notification.success({
                          message: 'Update Success',
                          placement: 'bottomLeft'
                        });
                        if (params.id) {
                          fetchTemplate(params.id);
                        } else {
                          navigate(
                            `/dashboard/template-management/template/${data.id}`,
                            {
                              replace: true
                            }
                          );
                        }
                      }}
                      loading={resultCreateTemplate.isLoading}
                      type="primary"
                      ghost
                    >
                      Save
                    </Button>
                    <Button
                      loading={resultCreateTemplate.isLoading}
                      type="primary"
                      onClick={async () => {
                        const values = form.getFieldsValue();
                        formValues.current[currentStep] = {
                          ...values
                        };
                        if (currentStep === 1) {
                          try {
                            await saveTemplate();
                            navigate('/dashboard/template-management');
                            form.resetFields();
                            notification.success({
                              message: 'Update Success',
                              placement: 'bottomLeft'
                            });
                          } catch (error) {
                            console.error('Save Template', error);
                          }
                        } else {
                          setCurrentStep(currentStep + 1);
                        }
                      }}
                    >
                      Next
                    </Button>
                  </Space>
                  <Caption>{lastSaved}</Caption>
                </Space>
              </StyledFooter>
              <Form.List name="scheme">
                {(fields) => {
                  return fields.map((field) => {
                    const section = form.getFieldValue('scheme')[field.key];
                    return (
                      <ModalForm
                        name={section.name}
                        configs={section.configs}
                        key={`scheme-${field.key}`}
                        title={section.value}
                        initialValues={{
                          value: section.value,
                          fields: section.fields,
                          configs: section.configs
                        }}
                        onCancel={() => {
                          setVisibleModal('');
                        }}
                        open={section.name === visibleModal}
                      ></ModalForm>
                    );
                  });
                }}
              </Form.List>
            </Form>
            <ModalSelectProduct
              selectedValue={form.getFieldValue('products')}
              open={isOpenProductModal}
              onCancel={() => {
                setIsOpenProductModal(false);
              }}
              onOk={(selectedProduct) => {
                form.setFieldValue('products', selectedProduct);
                setIsOpenProductModal(false);
              }}
            ></ModalSelectProduct>
          </Form.Provider>
        </Spin>
      )}
    </div>
  );
};

export default TemplateInformation;
